"use client";

import clsx from "clsx";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import type { Tone } from "@/lib/labels";

const TONE_CLASS: Record<Tone, string> = {
  good: "bg-good-soft text-good-ink",
  bad: "bg-bad-soft text-bad-ink",
  warn: "bg-warn-soft text-warn-ink",
  neutral: "bg-surface-2 text-ink-2",
  accent: "bg-accent-soft text-accent-ink",
};

type Variant = "primary" | "secondary" | "ghost" | "good" | "bad";
const VARIANT: Record<Variant, string> = {
  primary: "bg-accent text-white hover:brightness-110 disabled:opacity-50",
  secondary: "border border-line-strong bg-surface text-ink hover:bg-surface-2 disabled:opacity-50",
  ghost: "text-ink-2 hover:bg-surface-2 hover:text-ink disabled:opacity-50",
  good: "bg-good text-white hover:brightness-110 disabled:opacity-50",
  bad: "bg-bad text-white hover:brightness-110 disabled:opacity-50",
};

export function Button({ variant = "secondary", size = "md", className, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: "xs" | "sm" | "md" | "lg" }) {
  return (
    <button
      {...rest}
      className={clsx(
        "inline-flex cursor-pointer items-center justify-center gap-1.5 font-medium transition active:scale-[0.98] disabled:cursor-not-allowed",
        // the corners per size (one class each: two would leave the order of the stylesheet to decide)
        size === "xs" && "h-6 rounded-md px-2 text-[12px]",
        size === "sm" && "h-8 rounded-xl px-3 text-[13px]",
        size === "md" && "h-10 rounded-xl px-4 text-[14px]",
        size === "lg" && "h-12 rounded-xl px-5 text-[15px]",
        VARIANT[variant],
        className,
      )}
    />
  );
}

/** An on / off switch (its name for screen readers). */
export function Toggle({ on, label, onChange }: { on: boolean; label: string; onChange: (on: boolean) => void }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} onClick={() => onChange(!on)} className={clsx("relative h-4 w-7 shrink-0 cursor-pointer rounded-full transition", on ? "bg-accent" : "bg-line-strong")}>
      <span className={clsx("absolute top-0.5 h-3 w-3 rounded-full bg-white shadow transition", on ? "left-[14px]" : "left-0.5")} />
    </button>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={clsx("skeleton", className)} />;
}

export function ErrorBox({ children }: { children: ReactNode }) {
  return (
    <div role="alert" className="rounded-xl border border-bad/30 bg-bad-soft px-4 py-3 text-[13.5px] text-bad-ink">
      {children}
    </div>
  );
}

export function Notice({ children, tone = "neutral" }: { children: ReactNode; tone?: Tone }) {
  return <div className={clsx("rounded-xl px-4 py-3 text-[13px]", TONE_CLASS[tone])}>{children}</div>;
}

export function Field({ label, hint, children }: { label: ReactNode; hint?: ReactNode; children: ReactNode }) {
  return (
    <label className="block">
      <span className="text-[12.5px] font-medium text-ink-2">{label}</span>
      <div className="mt-1">{children}</div>
      {hint && <span className="mt-1 block text-[12px] text-muted">{hint}</span>}
    </label>
  );
}

export const inputClass =
  "h-10 w-full rounded-xl border border-line-strong bg-surface px-3 text-[14px] text-ink outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20 tabular";

/** A segmented control's sizes: its frame and its items ("xs": slim, for a toolbar of a watchlist's height). */
const SEGMENT_SIZE = {
  xs: { frame: "rounded-md p-0.5", item: "rounded px-1.5 text-[11.5px] leading-5" },
  sm: { frame: "rounded-xl p-1", item: "rounded-lg px-2.5 py-1 text-[12.5px]" },
  md: { frame: "rounded-xl p-1", item: "rounded-lg px-3.5 py-1.5 text-[13.5px]" },
} as const;

export function Segmented<T extends string>({ value, options, onChange, size = "md" }: { value: T; options: { value: T; label: string }[]; onChange: (v: T) => void; size?: keyof typeof SEGMENT_SIZE }) {
  return (
    <div role="tablist" className={clsx("inline-flex bg-surface-2", SEGMENT_SIZE[size].frame)}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={o.value === value}
          onClick={() => onChange(o.value)}
          className={clsx(
            "cursor-pointer font-medium transition",
            SEGMENT_SIZE[size].item,
            o.value === value ? "bg-surface text-ink shadow-card" : "text-muted hover:text-ink",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Like `Segmented`, but each option switches on and off by itself — none, one or several on (in the options' order).
 * An option on shows its own colour (`on`: its classes), one off is grey.
 */
export function SegmentedMulti<T extends string>({ values, options, onChange, label, size = "md" }: { values: readonly T[]; options: { value: T; label: string; on: string }[]; onChange: (v: T[]) => void; label: string; size?: keyof typeof SEGMENT_SIZE }) {
  const toggle = (v: T) => onChange(options.map((o) => o.value).filter((x) => (x === v) !== values.includes(x)));
  return (
    <div role="group" aria-label={label} className={clsx("inline-flex bg-surface-2", SEGMENT_SIZE[size].frame)}>
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={values.includes(o.value)} onClick={() => toggle(o.value)} className={clsx("cursor-pointer font-medium transition", SEGMENT_SIZE[size].item, values.includes(o.value) ? o.on : "text-muted hover:text-ink")}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** A row of a `MiniTable`: its cells (the first is the label column), optional tooltip and extra row classes. */
export interface MiniRow {
  key: string;
  cells: ReactNode[];
  title?: string;
  className?: string;
}

/**
 * The slim table of the side panel: an optional small title, always a header row with the column names, one
 * thin-ruled row per entry; columns after the first are right-aligned unless `left` names them. One look for every tab
 * (market, details, news, times, voting). `fill`: the column that takes the room the others leave and cuts its text
 * with "…" (its content brings `truncate`) — no width of its own (`max-w-0`), all the rest (`w-full`). `dense`: 11 px
 * instead of 12 (a table with many columns in the narrow side column).
 */
/** The small heading of a table section — also above a section that shows a note instead of rows. */
export function TableTitle({ children }: { children: ReactNode }) {
  return <h3 className="mb-1 text-[10.5px] font-semibold uppercase tracking-wide text-muted">{children}</h3>;
}

export function MiniTable({ title, head, rows, left = [], fill, dense = false }: { title?: ReactNode; head: ReactNode[]; rows: MiniRow[]; left?: number[]; fill?: number; dense?: boolean }) {
  const align = (k: number) => clsx(k === 0 || left.includes(k) ? "text-left" : "text-right", k === fill && "w-full max-w-0");
  return (
    <section className={dense ? "text-[11px]" : "text-[12px]"}>
      {title && <TableTitle>{title}</TableTitle>}
      <table className="w-full border-collapse tabular">
        <thead>
          <tr className="text-[10.5px] text-muted">
            {head.map((h, k) => <th key={k} className={clsx("pb-1 font-medium", k > 0 && "pl-2", align(k))}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} title={r.title} className={clsx("border-t border-line/60", r.className)}>
              {r.cells.map((c, k) => <td key={k} className={clsx("py-1 align-top", k > 0 && "pl-2", align(k))}>{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/** A trade's side in its colour: long green, short red. */
export const SIDE_TONE: Record<"long" | "short", string> = { long: "text-good-ink", short: "text-bad-ink" };
/** Text in its side's colour. */
export const Tinted = ({ side, children }: { side: "long" | "short"; children: ReactNode }) => <span className={SIDE_TONE[side]}>{children}</span>;
